import React, {useState, useEffect} from 'react';
import {
  Button,
  ButtonGroup,
  Classes,
  Dialog,
  DialogBody,
  Intent,
  InputGroup,
  Checkbox,
  Toaster,
  Position,
  Icon,
} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {useSelector} from "react-redux";
import {Tooltip2} from '@blueprintjs/popover2';

import {List, ListRow, ListColumn, ListHeaders} from '../../list';
import {IItem, SimpleSelect} from "../../select";
import {IState} from "../../../store/reducers";
import {LOCALE} from './constants';

import './ConvertColumnDatatype.scss';

const AppToaster = Toaster.create({
  position: Position.TOP,
});

interface ColumnProperty {
  id: string;
  show_in_table: boolean;
  label: string;
  width: string;
}

interface Columns {
  [id: string]: ColumnProperty;
}

const getColumns = (isCSV: boolean): Columns => ({
  name: {
    id: 'name',
    show_in_table: true,
    label: 'Name',
    width: '36%'
  },
  alias: {
    id: 'alias',
    show_in_table: true,
    label: 'New Name',
    width: '14%'
  },
  datatype: {
    id: 'datatype',
    show_in_table: !isCSV,
    label: 'Datatype',
    width: '10%'
  },
  new_datatype: {
    id: 'new_datatype',
    show_in_table: !isCSV,
    label: 'New Datatype',
    width: '30%'
  },
  position: {
    id: 'position',
    show_in_table: true,
    label: 'Position',
    width: '10%'
  },
  include: {
    id: 'include',
    show_in_table: true,
    label: 'Include',
    width: '5%'
  },
});

const DATA_TYPE_OPTIONS: IItem[] = [
  {label: 'Text', value: 'text'},
  {label: 'Number', value: 'number'},
  {label: 'Integer', value: 'integer'},
  {label: 'Float', value: 'float'},
  {label: 'Percentage', value: 'percentage'},
  {label: 'Currency ($)', value: 'currency_dol'},
  {label: 'Currency (€)', value: 'currency_eur'},
  {label: 'Date (fixed format: yyyy-mm-dd)', value: 'date'},
  {label: 'Date (unchanged format)', value: 'unchanged_date'},
  {label: 'Datetime', value: 'datetime'},
  {label: 'Boolean', value: 'boolean'},
];

type OnChangeCallback = (value: any) => void;

interface ConvertColumnDatatypeDialogProps {
  columnProperties: any;
  show: boolean;
  onClose: () => void;
  onDataTypesChange: (dataTypes: any) => void;
  disabled: boolean;
  isCSV: boolean;
  workbookLocale: string;
  onWorkbookLocaleChange: OnChangeCallback;
}

interface ColumnState {
  datatype: string;
  alias: string;
  position: number;
  include: boolean;
}

export const ConvertColumnDatatypeDialog = ({
  columnProperties,
  show,
  onClose,
  onDataTypesChange,
  disabled,
  isCSV,
  workbookLocale,
  onWorkbookLocaleChange,
}: ConvertColumnDatatypeDialogProps) => {
  const [updatedTypes, setUpdatedTypes] = useState<Record<string, ColumnState>>({});
  const viewData = useSelector((state: IState) => state.tableau.viewData);

  const columns = getColumns(isCSV);

  const getInitialColumnState = (columnName: string, index: number): ColumnState => {
    const columnProps = columnProperties?.[columnName] || {};
    const viewColumnProps = viewData?.column_properties?.[columnName] || {};

    return {
      datatype: columnProps.datatype || viewColumnProps.datatype || '',
      alias: columnProps.alias || viewColumnProps.alias || '',
      position: columnProps.position || viewColumnProps.position || index + 1,
      include: columnProps.include ?? viewColumnProps.include ?? true,
    };
  };

  useEffect(() => {
    if (!viewData?.column_properties || !viewData?.column_order) return;

    const mergedTypes = viewData.column_order.reduce((acc: any, column: string, index: number) => {
      acc[column] = getInitialColumnState(column, index);
      return acc;
    }, {} as Record<string, { datatype: string }>);

    setUpdatedTypes(mergedTypes);
  }, [viewData, columnProperties]);

  useEffect(() => {
    if (show && (!viewData?.column_order?.length || !Object.keys(viewData?.column_properties || {}).length)) {
      AppToaster.show({
        intent: Intent.WARNING,
        message: "This view does not contain any data to display or convert.",
      });
    }
  }, [show, viewData]);


  if (
    !show ||
    !viewData?.column_order?.length ||
    !Object.keys(viewData?.column_properties || {}).length
  ) {
    return null;
  }

  const handleDataTypeChange = (columnName: string, selectedOption: IItem) => {
    setUpdatedTypes((prev) => ({
      ...prev,
      [columnName]: {
        ...prev[columnName],
        datatype: selectedOption.value,
      },
    }));
  };

  const handleNameChange = (columnName: string, alias: string) => {
    setUpdatedTypes((prev) => ({
      ...prev,
      [columnName]: {
        ...prev[columnName],
        alias,
      },
    }));
  };

  const handlePositionChange = (columnName: string, position: string) => {
    setUpdatedTypes((prev) => ({
      ...prev,
      [columnName]: {
        ...prev[columnName],
        position: parseInt(position, 10),
      },
    }));
  }

  const handleIncludeChange = (columnName: string, event: React.FormEvent<HTMLInputElement>) => {
    const checked = (event.target as HTMLInputElement).checked;

    setUpdatedTypes((prev) => ({
      ...prev,
      [columnName]: {
        ...prev[columnName],
        include: checked,
      },
    }));
  };

  const onSave = () => {
    const finalDataTypes = viewData.column_order.reduce((acc: any, column: string) => {
      acc[column] = updatedTypes[column] || {
        datatype: viewData.column_properties[column].datatype,
        alias: viewData.column_properties[column].alias,
        position: viewData.column_properties[column].position,
        include: viewData.column_properties[column].include,
      }
      return acc;
    }, {} as Record<string, ColumnState>);

    onDataTypesChange(finalDataTypes);
    onClose();
  };

  return (
    <Dialog
      title="Change Column Datatype"
      className="convert-column-datatype-dialog"
      autoFocus={true}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
      enforceFocus={true}
      usePortal={true}
      isOpen={show}
      onClose={onClose}
      icon={IconNames.EDIT}
    >
      <DialogBody>
        {
          !isCSV &&
            <div className="convert-column-workbook-locale-label" style={{marginBottom: 20}}>
              <span className="locale-label-with-tip" style={{display: 'flex', alignItems: 'center', marginRight: 16}}>
                <span>Workbook Locale</span>
                <Tooltip2
                  content="This should match the locale setting of your Tableau workbook to ensure correct number and date parsing."
                  placement="right"
                  hoverOpenDelay={100}
                >
                  <Icon
                    icon={IconNames.INFO_SIGN}
                    intent="primary"
                    style={{cursor: 'pointer', marginLeft: 6}}
                    size={12}
                  />
                </Tooltip2>
              </span>
              <SimpleSelect
                selected={workbookLocale}
                options={LOCALE}
                onChange={onWorkbookLocaleChange}
                disabled={disabled}
                buttonProps={{
                  minimal: true,
                  fill: true,
                  small: true,
                  outlined: true,
                }}
                popoverProps={{
                  usePortal: true,
                  portalClassName: 'datatype-popup-popover',
                }}
              />
            </div>
        }
        {viewData?.column_order?.length > 0 && (
          <List>
            <ListHeaders
              headers={columns}
            />
            {
              viewData.column_order.map((columnName: string, index: number) => {
                return (
                  <ListRow key={columnName}>
                    {columns.name.show_in_table && (
                      <ListColumn properties={columns.name}>
                        <span className="bp4-text-overflow-ellipsis">{columnName}</span>
                      </ListColumn>
                    )}

                    {columns.alias.show_in_table && (
                      <ListColumn properties={columns.alias}>
                        <InputGroup
                          value={updatedTypes[columnName]?.alias ?? ''}
                          disabled={disabled}
                          onChange={(event) => handleNameChange(columnName, event.target.value)}
                          autoComplete="off"
                          type={"text"}
                        />
                      </ListColumn>
                    )}

                    {columns.datatype.show_in_table && (
                      <ListColumn
                        properties={columns.datatype}>{viewData.column_properties[columnName]['datatype']}
                      </ListColumn>
                    )}

                    {columns.new_datatype.show_in_table && (
                      <ListColumn properties={columns.new_datatype}>
                        <SimpleSelect
                          selected={updatedTypes[columnName]?.datatype}
                          onChange={(selectedOption) => handleDataTypeChange(columnName, selectedOption)}
                          options={DATA_TYPE_OPTIONS}
                          disabled={disabled}
                          buttonProps={{
                            minimal: true,
                            fill: true,
                            small: true,
                            outlined: true,
                          }}
                          popoverProps={{
                            usePortal: true,
                            portalClassName: 'datatype-popup-popover',
                          }}
                        />
                      </ListColumn>
                    )}

                    {columns.position.show_in_table && (
                      <ListColumn properties={columns.position}>
                        <InputGroup
                          value={updatedTypes[columnName]?.position.toString() ?? (index + 1).toString()}
                          disabled={disabled}
                          onChange={(event) => handlePositionChange(columnName, event.target.value)}
                          autoComplete="off"
                          type="number"
                          min={1}
                          max={viewData.column_order.length}
                        />
                      </ListColumn>
                    )}

                    {columns.include.show_in_table && (
                      <ListColumn properties={columns.include}>
                        <Checkbox
                          checked={updatedTypes[columnName]?.include ?? true}
                          disabled={disabled}
                          onChange={(event) => handleIncludeChange(columnName, event)}
                        />
                      </ListColumn>
                    )}
                  </ListRow>
                )
              })
            }
          </List>
        )}
      </DialogBody>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_MAIN_SECTION}>
        </div>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <ButtonGroup>
            <Button
              text="Close"
              onClick={onClose}
            />
            <Button
              intent={Intent.PRIMARY}
              text="Save"
              onClick={onSave}
            />
          </ButtonGroup>
        </div>
      </div>
    </Dialog>
  );
};

export default ConvertColumnDatatypeDialog;
